import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService<T> {
  private apiUrl = `${environment.apiUrl}`;

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) { }

  getHeaders(notAllowDataSource = false): HttpHeaders {
    const token = this.secureStorage.getItem('authToken') || '';

    let headers = new HttpHeaders({
      Accept: '*/*',
    });

    if (!notAllowDataSource) {
      headers = headers.set('x-data-source', environment.dataSource);
    }

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      console.warn('⚠️ No Auth Token Found in Local Storage!');
    }
    return headers;
  }

  add<T>(
    endpoint: string,
    data: T,
    options?: { headers?: HttpHeaders },
    notAllowDataSource = false
  ): Observable<T> {
    let headers = this.getHeaders(notAllowDataSource);

    if (options?.headers) {
      headers = headers.append(
        options.headers.keys().toString(),
        options.headers.get(options.headers.keys().toString()) || ''
      );
    }

    console.log(
      '🛠 Final Headers Sent:',
      headers.keys().map((key) => `${key}: ${headers.get(key)}`)
    );

    return this.http
      .post<T>(`${this.apiUrl}/${endpoint}`, data, { headers })
      .pipe(
        catchError((error) => {
          console.error('API Error:', error);
          return throwError(() => error);
        })
      );
  }

  getAll<T>(endpoint: string, skipLoader = false, notAllowDataSource = false): Observable<T[]> {
    let headers = this.getHeaders(notAllowDataSource);
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', 'true');
    }
    headers = headers
      .set('Cache-Control', 'no-cache, no-store, must-revalidate')
      .set('Pragma', 'no-cache');

    return this.http.get<T[]>(`${this.apiUrl}/${endpoint}`, { headers }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getAllByParams<T>(
    endpoint: string,
    options?: { params?: any; skipLoader?: boolean }
  ): Observable<T[]> {
    let headers = this.getHeaders();
    const params = options?.params;
    const hasPaginationParams =
      params &&
      (Object.prototype.hasOwnProperty.call(params, 'page') ||
        Object.prototype.hasOwnProperty.call(params, 'per_page') ||
        Object.prototype.hasOwnProperty.call(params, 'perPage') ||
        Object.prototype.hasOwnProperty.call(params, 'pageSize') ||
        Object.prototype.hasOwnProperty.call(params, 'page_size') ||
        Object.prototype.hasOwnProperty.call(params, 'limit') ||
        Object.prototype.hasOwnProperty.call(params, 'offset'));

    const shouldSkipLoader = options?.skipLoader || hasPaginationParams;
    if (shouldSkipLoader) {
      headers = headers.set('X-Skip-Loader', 'true');
    }
    headers = headers
      .set('Cache-Control', 'no-cache, no-store, must-revalidate')
      .set('Pragma', 'no-cache');
    const httpOptions = { headers, params };

    return this.http.get<T[]>(`${this.apiUrl}/${endpoint}`, httpOptions).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getById<T>(endpoint: string, id: number): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}/${id}`, {
      headers: this.getHeaders(),
    });
  }

  update<T>(endpoint: string, id: number, data: T, notAllowDataSource = false): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}/${id}`, data, {
      headers: this.getHeaders(notAllowDataSource),
    });
  }

  put<T>(endpoint: string, data: T): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders(),
    });
  }

  patch<T>(endpoint: string, id: number, data: Partial<T>): Observable<T> {
    return this.http
      .patch<T>(`${this.apiUrl}/${endpoint}/${id}`, data, {
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error) => {
          console.error('Patch API Error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * PATCH where the record id travels in the BODY rather than the path.
   * Xano endpoints such as `admin_model` expect { model_id, ... } in the body,
   * so `patch()` (which appends /:id to the URL) does not fit them.
   */
  patchWithBody<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .patch<T>(`${this.apiUrl}/${endpoint}`, body, {
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  deleteWithBody<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .request<T>('DELETE', `${this.apiUrl}/${endpoint}`, {
        body,
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  delete<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .request<T>('POST', `${this.apiUrl}/${endpoint}`, {
        body,
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  deleteRequest<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<T>(`${this.apiUrl}/${endpoint}`, {
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  deleteRequestById<T>(endpoint: string, Id: number, notAllowDataSource = false): Observable<T> {
    return this.http
      .delete<T>(`${this.apiUrl}/${endpoint}/${Id}`, {
        headers: this.getHeaders(notAllowDataSource),
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  approveReject<T>(endpoint: string, body: T): Observable<T> {
    return this.http
      .request<T>('POST', `${this.apiUrl}/${endpoint}`, {
        body,
        headers: this.getHeaders(),
      })
      .pipe(
        tap((response) => console.log('✅ API Response:', response)),
        catchError((error) => {
          console.error('🚨 Approve API Error:', error);
          return throwError(() => error);
        })
      );
  }
}
